import {groupBy, mapObject} from '@benchling/js-utils';
import _ from 'underscore';

import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  AccessibleGridCell,
  ContainableSummary,
  Container,
  ContainerSummary,
  GridSummary,
  ModalPane,
  TransferContainerSummary,
  TransferModalPane,
} from 'constants/SampleConstants';
import {SampleRestrictionStatus, SampleRoleParty, SampleStatusItem} from 'constants/SampleStatusConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import notifications from 'notifications';
import {CreateAndRegisterBioentityArgs} from 'registry/create-bioentity/CreateBioentityUtil';
import * as ContainerDAO from 'samples/ContainerDAO';
import * as StorableModalsUtil from 'samples/StorableModalsUtil';
import {
  IS_CONTAINER_TYPES,
  TYPE as InputSampleType,
} from 'samples/transfers/input-sample/InputSampleConstants';
import * as TransferInputPaneStateUtil from 'samples/transfers/input-sample/PaneStateUtil';
import {ContainerContentParam, ContainerTransferParam} from 'samples/transfers/TransferDAO';
import {ModalPreferences} from 'ui-library/ModalConstants';
import pick from 'util/pick';

export type MaybeNotYetCreatedContainer = Pick<
  TransferContainerSummary,
  'id' | 'name' | 'gridCellCoordinates' | 'quantity'
>;

export interface StorableModalsContainerTransfer extends ContainerTransferParam {
  id?: string;
  outputContainer: MaybeNotYetCreatedContainer;
}

export interface StorableModalsContainerContent extends ContainerContentParam {
  batch?: {id: string; name: string};
  container: MaybeNotYetCreatedContainer;
}

export interface TransferPaneSampleStatusState {
  canProceedOnSampleStatus: boolean;
  selectedSampleIds: Array<string>;
  shouldShowStatusWarnings: boolean;
  samples: Array<SampleStatusItem> | null;
  warningInputOutputType: InputSampleType | OutputSampleType | null;
}

export type UpdateTransferPaneSampleStatusStateHandler = (
  statusState: Partial<TransferPaneSampleStatusState>,
  statusStateKey: 'inputSampleStatusState' | 'outputSampleStatusState'
) => void;
export type UpdateTransferModalStateHandler = (state: Partial<TransferModalState>) => void;
export type OutputSampleType = ModelTypeConstants.CONTAINER | ModelTypeConstants.PLATE;
export interface ContainerSummaryWithContainedItemIds extends ContainerSummary {
  containedItemIds: Array<string>;
}

// Used for TransferModalPane panes
export interface TransferModalState {
  /**
   * Generic modal operations
   */
  pane: TransferModalPane;
  isTransferring: boolean;
  transferCancelled: boolean;
  errorMessage: string | null;
  // Errors that are not specific to a single pane, e.g. input/output conflicts, get rendered in a banner stack in child components
  errors: Array<string>;
  showNameTemplateBanner: boolean;

  /**
   * Used when creating a new input sample entity/batch to be transferred into output containers
   */
  createAndRegisterBioentityArgs: CreateAndRegisterBioentityArgs;

  /**
   * Transfer input state
   */
  inputType: InputSampleType;
  inputBioentity: BioentitySummary | null;
  inputContainer: ContainerSummaryWithContainedItemIds | null;
  inputContainable: ContainableSummary | null;
  inputContainableConcentration: LegacyMeasurement | null;
  initialOutputContainerStateById: {[id: string]: ContainerSummary};
  selectedInputContainer: ContainerSummaryWithContainedItemIds | null;
  inputSampleStatusState: TransferPaneSampleStatusState;

  /**
   * Transfer outputs state
   */
  outputType: OutputSampleType;
  outputPlate: GridSummary | null;
  outputCells: Array<AccessibleGridCell>; // Cells already include container contents
  outputContainerSummaries: Array<ContainerSummary>;
  outputContainers: Array<Container>; // We load the full containers for their contents
  selectedOutputContainers: Array<ContainerSummary>;
  // When specified, only update these containers. This lets you further narrow down the container
  // selection if needed, e.g. unchecking containers that you aren't authorized to use
  confirmedOutputContainerIds: Array<string> | null;
  outputSampleStatusState: TransferPaneSampleStatusState;

  /**
   * Finalized transfer quantities and all other transfer details
   */
  // used in ConfirmConcentrationsPane and when sending TransferAction requests to the backend
  containerContents: Array<StorableModalsContainerContent> | null;
  // used only when sending TransferAction requests to the backend
  containerTransfers: Array<StorableModalsContainerTransfer> | null;
  confirmTransferQuantitiesData: ConfirmTransferQuantitiesData | null;
  confirmConcentrationsData: ConfirmConcentrationsData | null;
  shouldCalculateConcentrations: boolean;
  areSolidUnitsInvolved: boolean;

  /**
   * Transfer sample controls state
   */
  useInferredSampleControls: boolean;
  restrictionStatus: SampleRestrictionStatus;
  restrictedSampleUsers: Array<SampleRoleParty>;
  sampleOwners: Array<SampleRoleParty>;
}

export type TransferModalPaneState =
  | ChooseTransferInputPaneState
  | ChooseTransferOutputPaneState
  | ConfirmTransferQuantitiesPaneState
  | ConfirmConcentrationsPaneState
  | ConfirmSampleControlsPaneState;

export type ChooseTransferInputPaneState = Pick<
  TransferModalState,
  | 'inputType'
  | 'inputBioentity'
  | 'createAndRegisterBioentityArgs'
  | 'inputContainer'
  | 'selectedOutputContainers'
  | 'inputSampleStatusState'
  | 'outputSampleStatusState'
  | 'confirmedOutputContainerIds'
  | 'showNameTemplateBanner'
  | 'outputCells'
  | 'outputContainerSummaries'
  | 'outputType'
  | 'errors'
>;

export type ChooseTransferOutputPaneState = Pick<
  TransferModalState,
  | 'outputType'
  | 'outputContainerSummaries'
  | 'outputPlate'
  | 'outputCells'
  | 'selectedInputContainer'
  | 'outputSampleStatusState'
  | 'inputSampleStatusState'
  | 'confirmedOutputContainerIds'
  | 'createAndRegisterBioentityArgs'
  | 'inputBioentity'
  | 'inputContainer'
  | 'inputType'
  | 'showNameTemplateBanner'
  | 'errors'
>;

export interface ConfirmTransferQuantitiesDataRow {
  outputContainer: Container;
  transferQuantityValue: number | null;
  finalOutputQuantityValue: number | null;
}

export type ConfirmTransferQuantitiesData = Array<ConfirmTransferQuantitiesDataRow>;

export type ConfirmTransferQuantitiesPaneState = Pick<
  TransferModalState,
  | 'confirmTransferQuantitiesData'
  | 'inputContainable'
  | 'selectedInputContainer'
  | 'inputContainableConcentration'
  | 'errorMessage'
  | 'initialOutputContainerStateById'
>;

export type ConfirmConcentrationsData = Array<{
  label: string;
  containerContents: Array<StorableModalsContainerContent>;
}>;

export type ConfirmConcentrationsPaneState = Pick<
  TransferModalState,
  'errorMessage' | 'shouldCalculateConcentrations' | 'confirmConcentrationsData' | 'areSolidUnitsInvolved'
>;

export type ConfirmSampleControlsPaneState = Pick<
  TransferModalState,
  | 'errorMessage'
  | 'useInferredSampleControls'
  | 'restrictionStatus'
  | 'restrictedSampleUsers'
  | 'sampleOwners'
  | 'selectedInputContainer'
  | 'confirmTransferQuantitiesData'
>;

export const TRANSFER_MODAL_PREFERENCES: {
  [preferenceType: string]: {preferenceKey: ModalPreferences; stateKey: keyof TransferModalState};
} = {
  INPUT_TYPE: {
    preferenceKey: ModalPreferences.TRANSFER_MODAL_INPUT_TYPE,
    stateKey: 'inputType',
  },
};

export function getTransferInputPaneState(
  transferModalState: TransferModalState
): ChooseTransferInputPaneState {
  return pick(
    transferModalState,
    'inputType',
    'inputBioentity',
    'createAndRegisterBioentityArgs',
    'inputContainer',
    'selectedOutputContainers',
    'inputSampleStatusState',
    'outputSampleStatusState',
    'confirmedOutputContainerIds',
    'showNameTemplateBanner',
    'outputCells',
    'outputContainerSummaries',
    'outputType',
    'errors'
  );
}

export function getTransferOutputPaneState(
  transferModalState: TransferModalState
): ChooseTransferOutputPaneState {
  return pick(
    transferModalState,
    'outputType',
    'outputContainerSummaries',
    'outputPlate',
    'outputCells',
    'selectedInputContainer',
    'outputSampleStatusState',
    'inputSampleStatusState',
    'confirmedOutputContainerIds',
    'createAndRegisterBioentityArgs',
    'inputBioentity',
    'inputContainer',
    'inputType',
    'showNameTemplateBanner',
    'errors'
  );
}

export function getTransferQuantitiesPaneState(
  transferModalState: TransferModalState
): ConfirmTransferQuantitiesPaneState {
  return pick(
    transferModalState,
    'confirmTransferQuantitiesData',
    'inputContainable',
    'selectedInputContainer',
    'inputContainableConcentration',
    'errorMessage',
    'initialOutputContainerStateById'
  );
}

export function getTransferConcentrationsPaneState(
  transferModalState: TransferModalState
): ConfirmConcentrationsPaneState {
  return pick(
    transferModalState,
    'errorMessage',
    'shouldCalculateConcentrations',
    'confirmConcentrationsData',
    'areSolidUnitsInvolved'
  );
}

export function getTransferSampleControlsPaneState(
  transferModalState: TransferModalState
): ConfirmSampleControlsPaneState {
  return pick(
    transferModalState,
    'errorMessage',
    'useInferredSampleControls',
    'restrictionStatus',
    'restrictedSampleUsers',
    'sampleOwners',
    'selectedInputContainer',
    'confirmTransferQuantitiesData'
  );
}

export function getTransferPaneState(
  paneKey: TransferModalPane,
  transferModalState: TransferModalState
): TransferModalPaneState {
  const {
    inputType,
    inputBioentity,
    createAndRegisterBioentityArgs,
    inputContainer,
    inputContainableConcentration,
    outputType,
    outputContainerSummaries,
    outputPlate,
    outputCells,
    confirmTransferQuantitiesData,
    containerContents,
    containerTransfers,
    errors,
    errorMessage,
    shouldCalculateConcentrations,
    inputSampleStatusState,
    outputSampleStatusState,
    confirmedOutputContainerIds,
    useInferredSampleControls,
    restrictionStatus,
    restrictedSampleUsers,
    sampleOwners,
    showNameTemplateBanner,
  } = transferModalState;

  const selectedInputContainer = IS_CONTAINER_TYPES.includes(inputType) ? inputContainer : null;
  const selectedOutputContainers =
    outputType === ModelTypeConstants.CONTAINER
      ? outputContainerSummaries
      : outputCells.map((cell) => cell.container);
  const containerCountsById = _.countBy(selectedOutputContainers, (container) => container.id);
  assert(
    Object.values(containerCountsById).every((count) => count === 1),
    'Expected only output container IDs to be unique'
  );
  const initialOutputContainerStateById = mapObject(
    groupBy(selectedOutputContainers, (container) => container.id),
    (containers) => containers[0]
  );
  switch (paneKey) {
    case ModalPane.CHOOSE_TRANSFER_INPUT:
      return {
        inputType,
        inputBioentity,
        createAndRegisterBioentityArgs,
        inputContainer,
        selectedOutputContainers,
        inputSampleStatusState,
        outputSampleStatusState,
        confirmedOutputContainerIds,
        showNameTemplateBanner,
        outputCells,
        outputContainerSummaries,
        outputType,
        errors,
      };
    case ModalPane.CHOOSE_TRANSFER_OUTPUT:
      return {
        outputType,
        outputContainerSummaries,
        outputPlate,
        outputCells,
        selectedInputContainer,
        outputSampleStatusState,
        inputSampleStatusState,
        confirmedOutputContainerIds,
        createAndRegisterBioentityArgs,
        inputBioentity,
        inputContainer,
        inputType,
        showNameTemplateBanner,
        errors,
      };
    case ModalPane.CONFIRM_TRANSFER_QUANTITIES:
      return {
        confirmTransferQuantitiesData,
        errorMessage,
        inputContainable: inputBioentity,
        inputContainableConcentration,
        selectedInputContainer,
        initialOutputContainerStateById,
      };
    case ModalPane.CONFIRM_CONCENTRATIONS:
      return StorableModalsUtil.getUpdatedConcentrationPaneState({
        containerTransfers,
        containerContents,
        shouldCalculateConcentrations,
        inputContainer,
        errorMessage,
      });
    case ModalPane.CONFIRM_SAMPLE_CONTROLS:
      return {
        errorMessage,
        useInferredSampleControls,
        restrictionStatus,
        restrictedSampleUsers,
        sampleOwners,
        selectedInputContainer,
        confirmTransferQuantitiesData,
      };
    default:
      throw new Error(`Unexpected transfer modal pane ${paneKey}`);
  }
}

export function isPaneComplete(paneKey: ModalPane, transferModalState: TransferModalState): boolean {
  switch (paneKey) {
    case ModalPane.CHOOSE_TRANSFER_INPUT:
      return StorableModalsUtil.isChooseTransferInputPaneComplete(transferModalState);
    case ModalPane.CHOOSE_TRANSFER_OUTPUT:
      return StorableModalsUtil.isChooseTransferOutputPaneComplete(transferModalState);
    case ModalPane.CONFIRM_TRANSFER_QUANTITIES: {
      const paneState = getTransferPaneState(
        paneKey,
        transferModalState
      ) as ConfirmTransferQuantitiesPaneState;
      return StorableModalsUtil.isConfirmTransferQuantitiesPaneComplete(paneState);
    }
    case ModalPane.CONFIRM_CONCENTRATIONS:
      return StorableModalsUtil.isConfirmConcentrationsPaneComplete(transferModalState);
    case ModalPane.CONFIRM_SAMPLE_CONTROLS:
      return StorableModalsUtil.isConfirmSampleControlsPaneComplete(transferModalState);
    default:
      return false;
  }
}

export function getConfirmedOutputContainers(
  outputCells: Array<AccessibleGridCell>,
  outputContainerSummaries: Array<ContainerSummary>,
  confirmedOutputContainerIds: Array<string> | null,
  outputType: OutputSampleType
): Array<ContainerSummary> {
  const outputContainers =
    outputType === ModelTypeConstants.PLATE
      ? outputCells.map((cell) => cell.container)
      : outputContainerSummaries;
  return confirmedOutputContainerIds == null
    ? outputContainers
    : outputContainers.filter((container) => new Set(confirmedOutputContainerIds).has(container.id));
}

export function getInputTagSchemaId(
  createAndRegisterBioentityArgs: CreateAndRegisterBioentityArgs,
  inputBioentity: BioentitySummary | null,
  inputType: InputSampleType
): string | null {
  switch (inputType) {
    case InputSampleType.NEW_BIOENTITY: {
      return createAndRegisterBioentityArgs.tagSchema?.id ?? null;
    }
    case InputSampleType.EXISTING_BIOENTITY: {
      return inputBioentity?.tagSchema?.id ?? null;
    }
    default:
      return null;
  }
}

export function initialConfirmTransferQuantitiesData(
  transferModalState: TransferModalState
): ConfirmTransferQuantitiesData {
  const {outputContainers, outputCells, outputType, confirmedOutputContainerIds, outputSampleStatusState} =
    transferModalState;
  let containers: Array<Container>;
  switch (outputType) {
    case ModelTypeConstants.CONTAINER: {
      assert(Boolean(outputContainers.length), 'Expected at least one output container');
      containers = outputContainers;
      break;
    }
    case ModelTypeConstants.PLATE: {
      assert(Boolean(outputCells.length), 'Expected at least one output cell');
      containers = outputCells.map(({container}) => container);
      break;
    }
    default:
      assert(false, `Unhandled output type ${outputType}`);
  }

  const confirmedOutputContainerIdsSet = new Set(confirmedOutputContainerIds);
  const maybeFilteredContainers =
    confirmedOutputContainerIds != null &&
    outputSampleStatusState.warningInputOutputType !== ModelTypeConstants.PLATE
      ? containers.filter((container) => confirmedOutputContainerIdsSet.has(container.id))
      : containers;

  return maybeFilteredContainers.map((container) => ({
    outputContainer: container,
    transferQuantityValue: null,
    finalOutputQuantityValue: null,
  }));
}

export function getContainerTransfers(
  transferModalState: TransferModalState
): Array<StorableModalsContainerTransfer> {
  const containerTransfers: Array<StorableModalsContainerTransfer> = [];

  const {inputBioentity, inputContainer, confirmTransferQuantitiesData} = transferModalState;
  const inputSampleId = TransferInputPaneStateUtil.getInputSample(inputBioentity, inputContainer)?.id;
  assert(inputSampleId != null, 'Expected input bioentity, batch or container to be defined');

  assert(confirmTransferQuantitiesData != null, 'expected confirmTransferQuantitiesData to be present');
  confirmTransferQuantitiesData.forEach(
    ({outputContainer, transferQuantityValue, finalOutputQuantityValue}) => {
      containerTransfers.push({
        inputSampleId,
        outputContainer,
        // Override the units to be null if the quantity value is null in order to allow the case of
        // doing a null transfer of a batch into a 0-quantity container. This will be unnecessary once
        // container quantity defaults to 0.
        quantity: {
          value: transferQuantityValue,
          units: transferQuantityValue == null ? null : outputContainer.quantity.units,
        },
      });
      // This second containerTransfer represents a dilution (added when final quantity > initial + transfer)
      // Treat null `finalQuantity` as "no dilution"
      const inferredTransferQuantityValue: number = transferQuantityValue ?? 0;
      const inferredOutputContainerQuantityValue: number = outputContainer.quantity.value ?? 0;
      if (
        finalOutputQuantityValue &&
        finalOutputQuantityValue > inferredTransferQuantityValue + inferredOutputContainerQuantityValue
      ) {
        containerTransfers.push({
          inputSampleId: null,
          outputContainer,
          quantity: {
            value:
              finalOutputQuantityValue - inferredTransferQuantityValue - inferredOutputContainerQuantityValue,
            units: outputContainer.quantity.units,
          },
        });
      }
    }
  );
  return containerTransfers;
}

export function getConfirmConcentrationsArgs(transferModalState: TransferModalState): {
  containerTransfers: Array<ContainerTransferParam>;
  shouldCalculateConcentrations: boolean;
  inputContainableConcentration: LegacyMeasurement | null;
} {
  const {shouldCalculateConcentrations, inputContainableConcentration, inputContainer} = transferModalState;
  const containerTransfers = getContainerTransfers(transferModalState);
  return {
    containerTransfers,
    shouldCalculateConcentrations: StorableModalsUtil.getShouldCalculateConcentrations(
      shouldCalculateConcentrations,
      containerTransfers,
      inputContainer
    ),
    inputContainableConcentration,
  };
}

export async function completeTransferInputPane(
  paneState: TransferModalState,
  goToNextPaneFn: () => void,
  updateTransferModalState: (state: Partial<TransferModalState>) => void
): Promise<void> {
  try {
    const modalState = await TransferInputPaneStateUtil.completeAndGetModalState(paneState);
    updateTransferModalState(modalState);
    goToNextPaneFn();
  } catch (e) {
    if (e instanceof TransferInputPaneStateUtil.DataModsModalCancelledException) {
      // Don't go to next pane
    } else {
      throw e;
    }
  }
}

/**
 * Used by TransferModal to fetch full dicts for those containers selected as transfer destinations.
 *
 * Containers are selected through StorableSearchInput, which only fetches summary dicts. Summary dicts lack
 * containeBatches information, which is required by TransferModal.
 */
export function bulkFetchContainers(
  containerIds: Array<string>,
  transferModalState: TransferModalState,
  updateTransferModalState: (state: Partial<TransferModalState>) => void
): void {
  ContainerDAO.bulkFetch(containerIds)
    .done(({containers}: ContainerDAO.BulkFetchContainersResponse) => {
      const stateWithOutputContainers = {...transferModalState, outputContainers: containers};
      const confirmTransferQuantitiesData = initialConfirmTransferQuantitiesData(stateWithOutputContainers);
      updateTransferModalState({outputContainers: containers, confirmTransferQuantitiesData});
    })
    .fail(notifications.notifyError);
}

export function getErrorBannerMessages(state: TransferModalState): Array<string> {
  const errorBannerMessages = [];

  if (hasInputOutputConflicts(state)) {
    errorBannerMessages.push('Please select different containers for the source and destination.');
  }

  return errorBannerMessages;
}

/**
 * Checks if the input and output containers are the same in the TransferModal.
 * @param TransferModalState state
 * @returns boolean
 */
export function hasInputOutputConflicts(state: TransferModalState): boolean {
  const {
    inputContainer,
    outputCells,
    outputContainerSummaries,
    outputType,
    selectedInputContainer,
    selectedOutputContainers,
  } = state;

  // In the "TRANSFER_GLOBAL" flow this condition is met after the user selects the input container in ChooseInputPane. In "TRANSFER_OUT" this condition is met immediately.
  if (selectedInputContainer != null) {
    if (
      outputType === ModelTypeConstants.CONTAINER &&
      outputContainerSummaries.some((container) => container.id === selectedInputContainer.id)
    ) {
      return true;
    }

    if (
      outputType === ModelTypeConstants.PLATE &&
      outputCells.some(({container}) => container.id === selectedInputContainer.id)
    ) {
      return true;
    }
  }

  // In the "TRANSFER_IN" flow this condition is met immediately.
  if (selectedOutputContainers.length > 0) {
    // Since selectedOutputContainers.length > 0, we're in the "TRANSFER_IN" flow and inputContainer must be defined
    if (
      inputContainer != null &&
      selectedOutputContainers.some((container) => container.id === inputContainer.id)
    ) {
      return true;
    }
  }

  return false;
}
